import { useCallback, useEffect, useState } from 'react'
import { del, get, patch, post } from '../lib/api'
import { t } from '../lib/i18n'

/**
 * The admin console shell.
 *
 * Everything here is CORE and appears in every Studio42 app. An application adds
 * its own tab alongside these; it does not modify them, because a project's
 * concept added here is inherited by every other project.
 */

type Tab = 'overview' | 'users' | 'broadcast' | 'devices' | 'errors' | 'flags' | 'audit' | 'keys' | 'ops'

const TABS: { id: Tab; label: string; capability: string }[] = [
  { id: 'overview',  label: 'Today',              capability: 'admin.access' },
  { id: 'users',     label: t('admin.users'),     capability: 'users.read' },
  { id: 'broadcast', label: t('admin.broadcast'), capability: 'notify.send' },
  { id: 'devices',   label: t('admin.devices'),   capability: 'notify.send' },
  { id: 'errors',    label: t('admin.errors'),    capability: 'errors.read' },
  { id: 'flags',     label: t('admin.flags'),     capability: 'flags.write' },
  { id: 'audit',     label: t('admin.audit'),     capability: 'audit.read' },
  // Owner-only. A token outlives a session and skips the CSRF boundary, so
  // issuing one is closer to changing a role than to sending a broadcast.
  { id: 'keys',      label: 'Keys',               capability: 'users.role' },
  { id: 'ops',       label: 'Ops',                capability: 'settings.write' },
]

interface Overview {
  app: string
  environment: string
  version: string
  commit: string
  users: { total: number; active: number; new_7d: number }
  openErrors: number
  notificationsSent7d: number
  overdueFlags: string[]
  recentActivity: { id: string; actor_handle: string | null; action: string; created_at: number }[]
  you: { handle: string; role: string; capabilities: string[] }
}

export default function Admin() {
  const [tab, setTab] = useState<Tab>('overview')
  const [overview, setOverview] = useState<Overview | null>(null)

  useEffect(() => {
    void get<Overview>('/api/admin/overview').then(setOverview).catch(() => setOverview(null))
  }, [])

  const caps = overview?.you.capabilities ?? []

  return (
    <section className="card">
      <h2>{t('admin.title')}</h2>

      <nav className="tabs">
        {/* A tab the role cannot use is absent, not disabled. */}
        {TABS.filter((x) => caps.includes(x.capability)).map((x) => (
          <button key={x.id} onClick={() => setTab(x.id)} aria-current={tab === x.id}>{x.label}</button>
        ))}
      </nav>

      {tab === 'overview' && <OverviewTab data={overview} onGo={setTab} />}
      {tab === 'users' && <UsersTab canSetRole={caps.includes('users.role')} />}
      {tab === 'broadcast' && <BroadcastTab />}
      {tab === 'devices' && <DevicesTab />}
      {tab === 'errors' && <ErrorsTab />}
      {tab === 'flags' && <FlagsTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'keys' && <KeysTab />}
      {tab === 'ops' && <OpsTab />}
    </section>
  )
}

/**
 * "Today" lands first and answers one question: what is waiting for me.
 *
 * The rule for a row here is that it must be ACTIONABLE and FINITE. A count that
 * only ever grows is a statistic, and statistics belong further in.
 */
function OverviewTab({ data, onGo }: { data: Overview | null; onGo: (t: Tab) => void }) {
  if (!data) return <p className="muted">{t('app.loading')}</p>
  return (
    <>
      <div className="stats">
        <button onClick={() => onGo('users')}><b>{data.users.total}</b><span>users</span></button>
        <button onClick={() => onGo('users')}><b>{data.users.new_7d}</b><span>new this week</span></button>
        <button onClick={() => onGo('errors')}><b>{data.openErrors}</b><span>open errors</span></button>
        <button onClick={() => onGo('broadcast')}><b>{data.notificationsSent7d}</b><span>notices sent</span></button>
      </div>

      {data.overdueFlags.length > 0 && (
        <p className="warn">
          Flags past their retirement date: {data.overdueFlags.join(', ')}. Remove the flag and the dead branch.
        </p>
      )}

      <h3>Recent activity</h3>
      <ul className="list">
        {data.recentActivity.map((a) => (
          <li key={a.id}>
            <div><strong>{a.action}</strong><div className="muted small">{a.actor_handle ?? 'system'}</div></div>
            <span className="muted small">{new Date(a.created_at).toLocaleString()}</span>
          </li>
        ))}
      </ul>

      <p className="muted small">{data.app} · {data.version} · {data.commit} · {data.environment}</p>
    </>
  )
}

interface AdminUser {
  id: string; handle: string; display_name: string; role: string; status: string
  passkeys: number; working_passkeys: number; created_at: number; last_seen_at: number | null
}

function UsersTab({ canSetRole }: { canSetRole: boolean }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const d = await get<{ users: AdminUser[]; roles: string[] }>('/api/admin/users')
    setUsers(d.users)
    setRoles(d.roles)
  }, [])
  useEffect(() => { void load() }, [load])

  async function change(id: string, body: Record<string, unknown>) {
    setError(null)
    try {
      await patch(`/api/admin/users/${id}`, body)
      await load()
    } catch (e) { setError((e as Error).message) }
  }

  return (
    <>
      {error && <p className="error" role="alert">{error}</p>}
      <ul className="list">
        {users.map((u) => (
          <li key={u.id}>
            <div>
              <strong>{u.display_name}</strong> <span className="muted">@{u.handle}</span>
              <div className="muted small">
                {/* Enrolled vs working: a passkey that has never been used never worked. */}
                {u.working_passkeys}/{u.passkeys} working passkeys
                {u.passkeys > 0 && u.working_passkeys === 0 && ' — enrolled but never used'}
                {u.passkeys === 0 && ' — no way in'}
              </div>
            </div>
            <div className="row">
              {canSetRole ? (
                <select value={u.role} onChange={(e) => change(u.id, { role: e.target.value })}>
                  {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              ) : (
                <span className="muted small">{u.role}</span>
              )}
              <button onClick={() => change(u.id, { status: u.status === 'active' ? 'suspended' : 'active' })}>
                {u.status === 'active' ? 'Suspend' : 'Restore'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

function BroadcastTab() {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState('all')
  const [result, setResult] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function send() {
    setBusy(true)
    setResult(null)
    try {
      const r = await post<{ users: number; devices: number }>('/api/admin/broadcast', { title, body, audience })
      // Devices, not people: a phone plus a laptop is two, and that is correct.
      setResult(`Sent to ${r.users} users across ${r.devices} devices.`)
      setTitle(''); setBody('')
    } catch (e) { setResult((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <>
      <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} /></label>
      <label>Message<textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={500} rows={3} /></label>
      <label>
        Audience
        <select value={audience} onChange={(e) => setAudience(e.target.value)}>
          <option value="all">Everyone</option>
          <option value="staff">Staff and above</option>
          <option value="admins">Admins and above</option>
          <option value="owner">Owner only</option>
        </select>
      </label>
      <button className="primary" disabled={busy || !title.trim() || !body.trim()} onClick={send}>Send</button>
      {result && <p className="muted">{result}</p>}
      <p className="muted small">
        Everyone gets an in-app notification. Push is a nudge on top, and only reaches devices that opted in.
      </p>
    </>
  )
}

interface Device { id: string; handle: string | null; platform: string; label: string | null; sent: number; shown: number; verdict: string; revoked_at: number | null }

function DevicesTab() {
  const [devices, setDevices] = useState<Device[]>([])
  const load = useCallback(async () => setDevices((await get<{ devices: Device[] }>('/api/admin/devices')).devices), [])
  useEffect(() => { void load() }, [load])

  return (
    <>
      <p className="muted small">This counts devices, not people. A phone and a laptop are two.</p>
      <ul className="list">
        {devices.map((d) => (
          <li key={d.id}>
            <div>
              <strong>{d.label ?? d.platform}</strong> <span className="muted">{d.handle ? `@${d.handle}` : 'anonymous'}</span>
              {/* The verdict, because "sent 12, shown 0" looks identical to healthy in a plain list. */}
              <div className="muted small">{d.verdict} · sent {d.sent}, shown {d.shown}</div>
            </div>
            {!d.revoked_at && (
              <button onClick={async () => { await del(`/api/admin/devices/${d.id}`); await load() }}>Forget</button>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

interface ClientError { fingerprint: string; message: string; source: string; url: string | null; version: string | null; count: number; last_seen: number }

function ErrorsTab() {
  const [errors, setErrors] = useState<ClientError[]>([])
  const load = useCallback(async () => setErrors((await get<{ errors: ClientError[] }>('/api/admin/errors')).errors), [])
  useEffect(() => { void load() }, [load])

  return (
    <ul className="list">
      {errors.length === 0 && <li className="muted">Nothing open.</li>}
      {errors.map((e) => (
        <li key={e.fingerprint}>
          <div>
            <strong>{e.message}</strong>
            <div className="muted small">
              ×{e.count} · {e.source} · {e.url} · {e.version} · {new Date(e.last_seen).toLocaleString()}
            </div>
          </div>
          <button onClick={async () => { await patch(`/api/admin/errors/${e.fingerprint}`, {}); await load() }}>
            Resolve
          </button>
        </li>
      ))}
    </ul>
  )
}

interface Flag { name: string; enabled: boolean; description: string; environment: string; retire_after: number | null }

function FlagsTab() {
  const [flags, setFlags] = useState<Flag[]>([])
  const load = useCallback(async () => setFlags((await get<{ flags: Flag[] }>('/api/admin/flags')).flags), [])
  useEffect(() => { void load() }, [load])

  return (
    <>
      <ul className="list">
        {flags.length === 0 && <li className="muted">No flags.</li>}
        {flags.map((f) => (
          <li key={f.name}>
            <div>
              <strong>{f.name}</strong>
              <div className="muted small">
                {f.description} · {f.environment}
                {f.retire_after && ` · retire by ${new Date(f.retire_after).toLocaleDateString()}`}
              </div>
            </div>
            <button onClick={async () => { await patch(`/api/admin/flags/${f.name}`, { enabled: !f.enabled }); await load() }}>
              {f.enabled ? 'On' : 'Off'}
            </button>
          </li>
        ))}
      </ul>
      <p className="muted small">
        A flag turns a shipped capability on and off. It is not a place to keep unfinished code,
        and every flag needs a retirement date.
      </p>
    </>
  )
}

interface Entry { id: string; actor_handle: string | null; action: string; resource: string | null; meta: string | null; created_at: number }

function AuditTab() {
  const [entries, setEntries] = useState<Entry[]>([])
  useEffect(() => { void get<{ entries: Entry[] }>('/api/admin/audit').then((d) => setEntries(d.entries)) }, [])

  return (
    <ul className="list">
      {entries.map((e) => (
        <li key={e.id}>
          <div>
            <strong>{e.action}</strong>
            <div className="muted small">{e.actor_handle ?? 'system'}{e.meta ? ` · ${e.meta}` : ''}</div>
          </div>
          <span className="muted small">{new Date(e.created_at).toLocaleString()}</span>
        </li>
      ))}
    </ul>
  )
}


interface TokenRow {
  id: string; label: string; token_prefix: string; scopes: string
  last_used_at: number | null; last_ip: string | null
  expires_at: number | null; revoked_at: number | null; created_at: number
}

/**
 * API tokens and third-party connections.
 *
 * The API for both has existed and been tested since the day they were written;
 * without a screen the capability was real and unusable, which is its own kind
 * of bug.
 */
function KeysTab() {
  const [tokens, setTokens] = useState<TokenRow[]>([])
  const [scopes, setScopes] = useState<string[]>([])
  const [connections, setConnections] = useState<Record<string, unknown>[]>([])
  const [label, setLabel] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [issued, setIssued] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [t, i] = await Promise.all([
      get<{ tokens: TokenRow[]; coreScopes: string[] }>('/api/admin/api-tokens'),
      get<{ connections: Record<string, unknown>[] }>('/api/admin/integrations').catch(() => ({ connections: [] })),
    ])
    setTokens(t.tokens)
    setScopes(t.coreScopes)
    setConnections(i.connections)
  }, [])
  useEffect(() => { void load() }, [load])

  async function issue() {
    setBusy(true); setError(null)
    try {
      const res = await post<{ token: string }>('/api/admin/api-tokens', { label, scopes: chosen })
      // Shown ONCE. Only the hash is stored, so there is no second chance and
      // saying so beside the value is the difference between a copied token and
      // a support message tomorrow.
      setIssued(res.token)
      setLabel(''); setChosen([])
      await load()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <>
      {error && <p className="error" role="alert">{error}</p>}

      {issued && (
        <div className="reveal">
          <strong>Copy this now — it cannot be shown again.</strong>
          <code>{issued}</code>
          <div className="row">
            <button onClick={() => navigator.clipboard?.writeText(issued).catch(() => {})}>Copy</button>
            <button onClick={() => setIssued(null)}>Done</button>
          </div>
        </div>
      )}

      <h3>Tokens</h3>
      <ul className="list">
        {tokens.length === 0 && <li className="muted">None issued.</li>}
        {tokens.map((t) => (
          <li key={t.id} className={t.revoked_at ? 'read' : ''}>
            <div>
              <strong>{t.label}</strong> <span className="muted mono small">{t.token_prefix}…</span>
              <div className="muted small">
                {JSON.parse(t.scopes || '[]').join(', ') || 'no scopes — can authenticate and do nothing'}
                {' · '}
                {/* The question this column exists for, before anyone revokes. */}
                {t.last_used_at ? `last used ${new Date(t.last_used_at).toLocaleDateString()}` : 'never used'}
                {t.expires_at ? ` · expires ${new Date(t.expires_at).toLocaleDateString()}` : ' · never expires'}
              </div>
            </div>
            {!t.revoked_at && (
              <button disabled={busy} onClick={async () => { await del(`/api/admin/api-tokens/${t.id}`); await load() }}>
                Revoke
              </button>
            )}
          </li>
        ))}
      </ul>

      <h3>New token</h3>
      <label>What is it for?<input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="zapier" /></label>
      <div className="chips">
        {scopes.map((s) => (
          <button key={s} className={chosen.includes(s) ? 'on' : ''}
            onClick={() => setChosen((c) => c.includes(s) ? c.filter((x) => x !== s) : [...c, s])}>{s}</button>
        ))}
      </div>
      <button className="primary" disabled={busy || !label.trim()} onClick={issue}>Issue it</button>

      <h3>Third-party connections</h3>
      <ul className="list">
        {connections.length === 0 && <li className="muted">Nothing connected.</li>}
        {connections.map((c, i) => (
          <li key={i}>
            <div>
              <strong>{String(c.provider)}</strong> <span className="muted small">{String(c.account_label)}</span>
              <div className="muted small">
                refreshed {Number(c.refresh_count)} times
                {c.last_error ? ` · last error: ${String(c.last_error)}` : ''}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * Backups, files and email — the three things whose absence is invisible.
 *
 * Every row here answers "is this actually working?" with a date or a count
 * rather than a yes. A backup reported as "configured" is the same answer one
 * that has never run would give.
 */
function OpsTab() {
  const [backups, setBackups] = useState<{ key: string; size: number; uploaded: string }[]>([])
  const [backupsOn, setBackupsOn] = useState(false)
  const [uploads, setUploads] = useState<Record<string, unknown>[]>([])
  const [emailState, setEmailState] = useState<{ provider: string; log: Record<string, unknown>[]; suppressions: Record<string, unknown>[] } | null>(null)

  useEffect(() => {
    void get<{ backups: typeof backups; configured: boolean }>('/api/admin/backups')
      .then((d) => { setBackups(d.backups); setBackupsOn(d.configured) }).catch(() => {})
    void get<{ uploads: Record<string, unknown>[] }>('/api/admin/uploads').then((d) => setUploads(d.uploads)).catch(() => {})
    void get<{ provider: string; log: Record<string, unknown>[]; suppressions: Record<string, unknown>[] }>('/api/admin/email')
      .then(setEmailState).catch(() => {})
  }, [])

  return (
    <>
      <h3>Backups</h3>
      {!backupsOn && <p className="warn">No bucket bound — nothing is being saved.</p>}
      <ul className="list">
        {backupsOn && backups.length === 0 && (
          // "Configured" and "has never run" look identical from the outside.
          <li className="warn">Configured, but nothing has been written yet.</li>
        )}
        {backups.slice(0, 7).map((b) => (
          <li key={b.key}>
            <div><strong>{b.key.split('/')[1]?.replace('.json', '')}</strong>
              <div className="muted small">{(b.size / 1024).toFixed(1)} KB</div></div>
            <span className="muted small">{new Date(b.uploaded).toLocaleString()}</span>
          </li>
        ))}
      </ul>

      <h3>Email</h3>
      <p className="muted small">
        Provider: <strong>{emailState?.provider ?? '—'}</strong>
        {emailState?.provider === 'console' && ' — logging, not sending'}
      </p>
      <ul className="list">
        {(emailState?.log ?? []).slice(0, 6).map((e, i) => (
          <li key={i}>
            <div><strong>{String(e.subject)}</strong>
              <div className="muted small">{String(e.to_address)} · {String(e.kind)}</div></div>
            <span className={String(e.status) === 'sent' ? 'muted small' : 'error'}>{String(e.status)}</span>
          </li>
        ))}
        {(emailState?.log ?? []).length === 0 && <li className="muted">Nothing sent.</li>}
      </ul>
      {(emailState?.suppressions ?? []).length > 0 && (
        <p className="muted small">{emailState!.suppressions.length} suppressed address(es) — bounced, complained or opted out.</p>
      )}

      <h3>Files</h3>
      <ul className="list">
        {uploads.length === 0 && <li className="muted">Nothing uploaded.</li>}
        {uploads.slice(0, 8).map((u, i) => (
          <li key={i}>
            <div><strong>{String(u.filename)}</strong>
              <div className="muted small">{String(u.content_type)} · {(Number(u.size_bytes) / 1024).toFixed(0)} KB · {String(u.purpose)}</div></div>
          </li>
        ))}
      </ul>
    </>
  )
}
